/** @format */
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { SessionStorageConfig } from '@shared/data-access/interfaces';

export const DEFAULT_SESSION_STORAGE_CONFIG: SessionStorageConfig = {
	allowNull: true,
	prefix: 'DCSG_NGX_'
};

@Injectable({
	providedIn: 'root'
})
export class SessionStorageService {
	readonly ERROR_MESSAGE_PREFIX = 'SESSION_STORAGE_ERROR:';
	private _prefix = '';
	private _allowNull = true;

	constructor(
		@Optional() @Inject('sessionStorageConfig') private config: SessionStorageConfig,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {
		if (this.config) {
			this._prefix =
				typeof config.prefix !== 'undefined' && config.prefix !== null
					? config.prefix
					: DEFAULT_SESSION_STORAGE_CONFIG.prefix;
			this._allowNull =
				typeof config.allowNull !== 'undefined' && config.allowNull !== null
					? config.allowNull
					: DEFAULT_SESSION_STORAGE_CONFIG.allowNull;
		} else {
			this._prefix = DEFAULT_SESSION_STORAGE_CONFIG.prefix;
			this._allowNull = DEFAULT_SESSION_STORAGE_CONFIG.allowNull;
		}
	}

	/**
	 * @description
	 * Gets the entry specified by the given key or null
	 *
	 * @param key - Key identifying the wanted entry
	 * @param prefix - Optional prefix to overwrite the configured one
	 * @returns The value for a given key/prefix
	 */
	get(key: string, prefix?: string): string | null | undefined {
		if (!isPlatformBrowser(this.platformId)) {
			return null;
		}

		prefix = typeof prefix !== 'undefined' && prefix !== null ? prefix : this._prefix;

		try {
			let returnValue = sessionStorage.getItem(`${prefix}${key}`);

			// Check for string "null"
			if (returnValue === 'null') {
				returnValue = null;
			}

			// Check for string "undefined"
			if (returnValue === 'undefined') {
				returnValue = undefined;
			}

			return returnValue;
		} catch (error) {
			console.error(`${this.ERROR_MESSAGE_PREFIX}${error.message}`);
		}
	}

	/**
	 * @description
	 * Removes the entry specified by the given key
	 *
	 * @param key - Key identifying the entry to remove
	 * @param prefix - Optional prefix to overwrite the configured one
	 */
	remove(key: string, prefix?: string): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		prefix = typeof prefix !== 'undefined' && prefix !== null ? prefix : this._prefix;

		try {
			sessionStorage.removeItem(`${prefix}${key}`);
		} catch (error) {
			console.error(`${this.ERROR_MESSAGE_PREFIX}${error.message}`);
		}
	}

	/**
	 * @description
	 * Clears all entries of the applications session storage
	 */
	removeAll(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		try {
			sessionStorage.clear();
		} catch (error) {
			console.error(`${this.ERROR_MESSAGE_PREFIX}${error.message}`);
		}
	}

	/**
	 * @description
	 * Adds tha value with the given key or updates an existing entry
	 *
	 * @param key - Key to store
	 * @param value - Value to store
	 * @param prefix - Optional prefix to overwrite the configured one
	 */
	set(key: string, value: string, prefix?: string): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		prefix = typeof prefix !== 'undefined' && prefix !== null ? prefix : this._prefix;

		if (
			this._allowNull ||
			(!this._allowNull && value !== 'null' && value !== null && value !== undefined)
		) {
			try {
				sessionStorage.setItem(`${prefix}${key}`, value);
			} catch (error) {
				console.error(`${this.ERROR_MESSAGE_PREFIX}${error.message}`);
			}
		} else {
			this.remove(key, prefix);
		}
	}
}
